import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ShieldHalf, Lock, Mail, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";

// Hidden platform control-plane login. Only super_admin accounts may proceed.
export default function PlatformLogin() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user !== false && user.role === "super_admin") {
    return <Navigate to="/platform" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      if (u?.role !== "super_admin") {
        await logout();
        setError("This account is not authorized for the platform console.");
        setSubmitting(false);
        return;
      }
      navigate("/platform", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0E17] px-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, #38BDF8 1px, transparent 0)", backgroundSize: "28px 28px",
      }} />
      <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 h-72 w-[36rem] bg-cyan-500/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20 mb-4">
            <ShieldHalf className="h-7 w-7 text-white" />
          </div>
          <div className="text-white font-bold text-[19px] tracking-tight">Platform Console</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-[0.2em] mt-1">ServiceGuru Pro · Control plane</div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4 bg-white/[0.03] border border-white/10 rounded-2xl p-6 backdrop-blur-sm" data-testid="platform-login-form">
          <div>
            <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Email</label>
            <div className="relative">
              <Mail className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email" required autoFocus value={email}
                data-testid="platform-login-email"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="platform@servicegurupro.com"
                className="w-full h-11 pl-9 pr-3 rounded-lg bg-[#0F1623] border border-white/10 text-[13.5px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/50"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Password</label>
            <div className="relative">
              <Lock className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="password" required value={password}
                data-testid="platform-login-password"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-11 pl-9 pr-3 rounded-lg bg-[#0F1623] border border-white/10 text-[13.5px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/50"
              />
            </div>
          </div>

          {error && (
            <div data-testid="platform-login-error" className="px-3 py-2 rounded-md bg-rose-500/10 border border-rose-500/30 text-[12.5px] text-rose-300">{error}</div>
          )}

          <button
            type="submit" disabled={submitting}
            data-testid="platform-login-submit"
            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[14px] font-semibold disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldHalf className="h-4 w-4" />}
            Access console
          </button>
        </form>
        <div className="text-center text-[11px] text-slate-600 mt-6">Restricted area · Authorized personnel only</div>
      </div>
    </div>
  );
}
